import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, GitBranch, Brain, Zap, Database, Search, TrendingUp } from 'lucide-react';

const GraphRAGResearch = ({ onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [hoveredImage, setHoveredImage] = useState(null);

  const researchImages = [
    {
      src: '/pic/innovation/2024/graphrag/graph_chunk.webp',
      title: 'Graph & Chunk Structure',
      description: 'How GraphRAG connects entities with text chunks'
    },
    {
      src: '/pic/innovation/2024/graphrag/community.webp',
      title: 'Community Detection',
      description: 'Hierarchical community summarization'
    },
    {
      src: '/pic/innovation/2024/graphrag/global_query.webp',
      title: 'Global Search',
      description: 'Community-level query processing'
    },
    {
      src: '/pic/innovation/2024/graphrag/local_query.webp',
      title: 'Local Search',
      description: 'Entity-focused local retrieval'
    },
    {
      src: '/pic/innovation/2024/graphrag/hippo_graph.webp',
      title: 'HippoRAG Graph',
      description: 'Neuroscience-inspired knowledge graph'
    },
    {
      src: '/pic/innovation/2024/graphrag/hippo_relationship.webp',
      title: 'Triple Extraction',
      description: 'Subject-Predicate-Object relationships'
    }
  ];

  const frameworks = [
    {
      name: 'LazyGraphRAG',
      icon: Zap,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'from-blue-500/20 to-cyan-500/20',
      borderColor: 'border-blue-500/30',
      title: 'Microsoft Azure Edge RAG Recommended',
      core: 'Build-on-Demand',
      features: [
        'No pre-computation of all summaries or embeddings',
        'Generate information graph dynamically at query time',
        'Dramatically reduce preprocessing costs',
        'Connect cross-source data through incremental browsing'
      ]
    },
    {
      name: 'HippoRAG',
      icon: Brain,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'from-purple-500/20 to-pink-500/20',
      borderColor: 'border-purple-500/30',
      title: 'Neuroscience-Inspired',
      core: 'Hippocampus Simulation',
      features: [
        'Use PPR algorithm for graph traversal',
        'Achieve non-parametric continual learning',
        'Excellent performance in multi-hop QA tasks',
        'Cost only a fraction of iterative RAG'
      ]
    },
    {
      name: 'LightRAG',
      icon: Database,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'from-green-500/20 to-emerald-500/20',
      borderColor: 'border-green-500/30',
      title: 'Efficient Graph Indexing',
      core: 'Dual-Level Retrieval',
      features: [
        'Combine vector retrieval with graph structure',
        'Leverage dual-key system for faster retrieval',
        'Fast indexing speed',
        'Support Ollama protocol for quick deployment'
      ]
    }
  ];

  const keyFindings = [
    {
      icon: TrendingUp,
      title: 'Answer Quality Improvement',
      description: 'HippoRAG outperforms traditional VectorRAG, especially in handling complex relationships and multi-hop questions',
      color: 'text-green-400'
    },
    {
      icon: Search,
      title: 'Retrieval Logic Optimization',
      description: 'Using LLM for element extraction performs better than community aggregation, providing more precise context',
      color: 'text-blue-400'
    },
    {
      icon: Database,
      title: 'Cost Considerations',
      description: 'LazyGraphRAG costs 0.1% of GraphRAG, HippoRAG about 8%, practical testing still needs optimization',
      color: 'text-purple-400'
    }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % researchImages.length);
    setHoveredImage(null);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + researchImages.length) % researchImages.length);
    setHoveredImage(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="modal-content max-w-6xl max-h-[90vh] overflow-y-auto pr-2 custom-scrollbar"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-btn" onClick={onClose}>×</button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <GitBranch className="w-8 h-8 text-cyan-400" />
            <h2 className="text-4xl font-bold">GraphRAG Research Journey</h2>
          </div>
          <p className="text-gray-300 text-lg">
            From Traditional RAG to Knowledge Graph-Augmented Retrieval
          </p>
        </div>



        {/* Three Frameworks Comparison */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold mb-6 text-center">🏗️ Framework Comparison</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {frameworks.map((framework, index) => {
              const Icon = framework.icon;
              return (
                <motion.div
                  key={framework.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-gradient-to-br ${framework.bgColor} rounded-xl p-6 border ${framework.borderColor} hover:border-white/40 transition-all`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`bg-gradient-to-br ${framework.color} p-3 rounded-lg`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">{framework.name}</h4>
                      <p className="text-xs text-gray-400">{framework.title}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs text-gray-400 mb-1">Core Logic</p>
                    <p className={`text-sm font-bold bg-gradient-to-r ${framework.color} bg-clip-text text-transparent`}>
                      {framework.core}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-400 mb-2">Key Features</p>
                    <ul className="space-y-1">
                      {framework.features.map((feature, idx) => (
                        <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
                          <span className="text-cyan-400 mt-0.5">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Key Findings */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold mb-6 text-center">📊 Key Research Findings</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {keyFindings.map((finding, index) => {
              const Icon = finding.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/5 rounded-xl p-5 border border-white/10 hover:border-white/20 transition-all"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-white/10 p-2 rounded-lg">
                      <Icon className={`w-5 h-5 ${finding.color}`} />
                    </div>
                    <h4 className={`font-bold ${finding.color}`}>{finding.title}</h4>
                  </div>
                  <p className="text-sm text-gray-300">{finding.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Research Insights */}
        <div className="mb-6">
          <h3 className="text-2xl font-bold mb-4 text-center">💡 Research Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: GraphRAG Overview */}
            <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl p-6 border border-cyan-500/30">
              <h4 className="font-bold text-cyan-400 mb-3 flex items-center gap-2">
                <GitBranch className="w-5 h-5" />
                GraphRAG Core Advantages
              </h4>
              <div className="space-y-3 text-sm text-gray-300">
                <p>
                  <span className="text-cyan-400 font-bold">Traditional VectorRAG:</span>
                  Relies solely on semantic matching of text chunks, performs poorly on questions requiring holistic answers
                </p>
                <p>
                  <span className="text-cyan-400 font-bold">GraphRAG Innovation:</span>
                  Extracts concepts from text chunks and connects them, considering related concept texts during queries
                </p>
                <p>
                  <span className="text-cyan-400 font-bold">Knowledge Graph:</span>
                  Besides text chunks, stores entities and their relationship edges, building a concept network
                </p>
                <div className="mt-4 p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
                  <p className="text-xs text-center">
                    ✨ Building knowledge graphs from sufficient documents can reveal knowledge connections that might be missed in daily work
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Cost & Performance */}
            <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
              <h4 className="font-bold text-purple-400 mb-3 flex items-center gap-2">
                <Database className="w-5 h-5" />
                Cost & Performance Evaluation
              </h4>
              <div className="space-y-3 text-sm text-gray-300">
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-purple-400 font-bold mb-1">LazyGraphRAG</p>
                  <p className="text-xs">Uses probabilistic and community algorithms instead of LLM summarization, dramatically reducing token costs</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-green-400 font-bold mb-1">HippoRAG</p>
                  <p className="text-xs">Excellent performance in multi-hop QA tasks, PPR algorithm achieves efficient graph traversal</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-blue-400 font-bold mb-1">Actual Test Results</p>
                  <p className="text-xs">Comparing same questions, HippoRAG answer quality outperforms VectorRAG, especially in complex relationship handling</p>
                </div>
                <div className="mt-3 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                  <p className="text-xs text-center">
                    📈 Indexing Phase: HippoRAG requires 150k tokens (approx 1.5x LazyGraphRAG)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Research Images Gallery - Stacked Horizontal */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold mb-4 text-center">🔍 Research Visualizations</h3>
          <div
            className="relative bg-gradient-to-br from-gray-900/50 to-gray-800/50 rounded-xl p-8 overflow-hidden"
            style={{ height: '450px' }}
          >
            {/* Navigation Arrows */}
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-30 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-all"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-30 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-all"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>

            {/* Stacked Images Container */}
            <div className="absolute inset-0 flex items-center justify-center" style={{ paddingLeft: '60px', paddingRight: '60px' }}>
              {researchImages.map((img, index) => {
                // Calculate offset from current image
                let offset = index - currentImageIndex;

                // Only show images within range
                if (offset < -2 || offset > 2) return null;

                // Stacked positioning: each image overlaps 75-80% of previous
                // Current image at center, others offset to the right
                const positionOffset = offset * 120; // 120px = 20% of 480px width
                const baseZIndex = 10 - Math.abs(offset);

                // Scale and opacity based on offset
                const scale = offset === 0 ? 1 : 0.85 - (Math.abs(offset) * 0.1);
                const opacity = offset === 0 ? 1 : 0.7 - (Math.abs(offset) * 0.2);

                return (
                  <motion.div
                    key={index}
                    className="absolute"
                    style={{
                      left: `calc(50% + ${positionOffset}px)`,
                      top: '50%',
                      x: '-50%',
                      y: '-50%',
                      zIndex: hoveredImage === index ? 25 : baseZIndex,
                    }}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{
                      scale: hoveredImage === index ? 1.08 : scale,
                      opacity: hoveredImage === null ? opacity : (hoveredImage === index ? 1 : 0.2),
                      zIndex: hoveredImage === index ? 25 : (hoveredImage === null ? baseZIndex : 5)
                    }}
                    transition={{ duration: 0.3 }}
                    onHoverStart={() => setHoveredImage(index)}
                    onHoverEnd={() => setHoveredImage(null)}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <div
                      className="bg-gray-900 rounded-xl shadow-2xl overflow-hidden border-2 border-cyan-500/30 cursor-pointer"
                      style={{
                        width: '480px',
                        maxHeight: '360px'
                      }}
                    >
                      <img
                        src={img.src}
                        alt={img.title}
                        className="w-full h-full object-contain"
                        style={{ maxHeight: '300px' }}
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                        <h4 className="font-bold text-cyan-400 text-sm">{img.title}</h4>
                        <p className="text-xs text-gray-300 mt-1">{img.description}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Image Counter */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/80 px-4 py-2 rounded-full border border-white/20">
              <span className="text-white text-sm font-bold">
                {currentImageIndex + 1} / {researchImages.length}
              </span>
            </div>
          </div>
          <p className="text-center text-gray-400 text-sm mt-2">
            💡 Hover to zoom • Click cards or use arrows to navigate
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default GraphRAGResearch;
